/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {ReactElement} from 'react';
import VisibilitySensor from 'react-visibility-sensor';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {SensorTypes, VehicleWithTrailerTires, VehicleWithTrailerWheels} from '@ion/core/src/types/entities';
import {dls, dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {tireSectionContent} from '@ion/features/src/smarterTrailer/TiresSection';
import Tooltip from '../components/tooltip/Tooltip';
import useMedia from '../hooks/useMedia';
import {useThemeMode} from '../theme/useThemeContext';
import {DLSTheme} from '../types/types';
import {
  colorTransition,
  generateEvents,
  getItemColor,
  getSensorItemMapping
} from './VehicleWithTrailerIcon';

interface SmartTrailerIconProps {
  trailerAxles?: number;
  onTireClick?: (tire: VehicleWithTrailerTires) => void;
  onWheelClick?: (tire: VehicleWithTrailerWheels) => void;
  onTireMouseOver?: (tire: VehicleWithTrailerTires) => void;
  onWheelMouseOver?: (tire: VehicleWithTrailerTires) => void;
  onTireMouseOut?: () => void;
  onWheelMouseOut?: () => void;
  clickedItem?: VehicleWithTrailerTires | VehicleWithTrailerWheels;
  hoveredItem?: VehicleWithTrailerTires | VehicleWithTrailerWheels;
  issues?: VehicleWithTrailerTires[] | VehicleWithTrailerWheels[];
  isSectionOpen?: boolean;
  isSideSheetOpen?: boolean;
  anchorEl?: HTMLAnchorElement;
  tooltipContainer?: HTMLElement;
  sensorType?: SensorTypes;
}

interface TooltipWrapperProps {
  children: ReactElement<any, any>;
  open: boolean;
  title: string;
  anchorEl: HTMLAnchorElement;
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  tooltipPlacementTop : {
    ...dlsAlt('typography', 'label2'),
    backgroundColor : dlsColor('grey1'),
    color           : dlsColor('white'),
    minWidth        : '100px',
    minHeight       : '32px',
    display         : 'flex',
    alignItems      : 'center',
    justifyContent  : 'center',
    top             : '2px',
    opacity         : 1
  },
  popper : {
    zIndex : '4000 !important' as any
  }
}));

/**
 * Wraps the tooltip component to keep consistent behavior between all
 * the tooltips in this component
 */
const TooltipWrapper = ({children, open, title, anchorEl}: TooltipWrapperProps) => {
  const classes   = useStyles();
  const {isPhone} = useMedia();
  const container = document.getElementById(tireSectionContent);

  if (!isPhone) {
    return (
      <VisibilitySensor offset={{top: 240}}>
        {({isVisible}) => {
          return (
            <Tooltip arrow
              open={isVisible && open}
              title={title}
              placement='top'
              PopperProps={{
                modifiers : {
                  preventOverflow : {
                    rootBoundary : 'viewport',
                    enabled      : true,
                    padding      : 8
                  }
                },
                anchorEl,
                container
              }}
              classes={{
                tooltipPlacementTop : classes.tooltipPlacementTop,
                popper              : classes.popper
              }}>
              {children}
            </Tooltip>
          );
        }}
      </VisibilitySensor>
    );
  }
  return <>{children}</>;
};

/**
 * Defines the Design Language System (DLS) Smart Trailer Icon Icon component.
 *
 * Independent trailer icon that can be coupled with the VehicleAxis Icon
 * to show a full tractor trailer or the the trailer by itself. Will show at
 * least 1 axle but up to 2. Provides callbacks for tire hovers and default
 * hover state for the tires to focus on hovered tire.
 *
 * ```typescript
 * import {SmartTrailerIcon} from '@ion/dls/src/icons/';
 *
 * <SmartTrailerIcon trailerAxles={trailerAxles}
 *   issues={issues}
 *   onTireHover={onHover}
 *   clickedItem={clickedTire} />
 * ```
 */
export const SmartTrailerIcon = ({
  trailerAxles = 1,
  onTireClick,
  onTireMouseOver,
  onTireMouseOut,
  clickedItem,
  hoveredItem,
  issues = [],
  anchorEl,
  sensorType = SensorTypes.TPMS
}: SmartTrailerIconProps) => {
  // re-renders on a theme mode change so the outline colors re-resolve ...
  useThemeMode();
  const style               = {cursor: 'pointer'};
  const isTPMS              = sensorType === SensorTypes.TPMS;
  const isWETS              = sensorType === SensorTypes.WETS;
  const white               = dlsColor('white');
  const grey4               = dlsColor('grey4');
  const grey6               = dlsColor('grey6');
  // built once per render, not once per tire or wheel color ...
  const sensorItemMapping   = getSensorItemMapping();

  /* TIRES */
  const mouseTireEvent      = generateEvents(onTireClick, onTireMouseOver);
  const onFrontLeftOutside  = mouseTireEvent(VehicleWithTrailerTires.TrailerFrontLeftOutside);
  const onBackLeftOutside   = mouseTireEvent(VehicleWithTrailerTires.TrailerBackLeftOutside);
  const onFrontRightInside  = mouseTireEvent(VehicleWithTrailerTires.TrailerFrontRightInside);
  const onBackRightInside   = mouseTireEvent(VehicleWithTrailerTires.TrailerBackRightInside);
  const onFrontLeftInside   = mouseTireEvent(VehicleWithTrailerTires.TrailerFrontLeftInside);
  const onBackLeftInside    = mouseTireEvent(VehicleWithTrailerTires.TrailerBackLeftInside);
  const onFrontRightOutside = mouseTireEvent(VehicleWithTrailerTires.TrailerFrontRightOutside);
  const onBackRightOutside  = mouseTireEvent(VehicleWithTrailerTires.TrailerBackRightOutside);

  const onGetTireColor = (tire) => {
    return getItemColor({
      type        : 'tire',
      item        : tire,
      issue       : issues.includes(tire),
      clickedItem : hoveredItem || clickedItem
    }, sensorItemMapping);
  };

  const colorFrontLeftOutside  = onGetTireColor(VehicleWithTrailerTires.TrailerFrontLeftOutside);
  const colorBackLeftOutside   = onGetTireColor(VehicleWithTrailerTires.TrailerBackLeftOutside);
  const colorFrontRightInside  = onGetTireColor(VehicleWithTrailerTires.TrailerFrontRightInside);
  const colorBackRightInside   = onGetTireColor(VehicleWithTrailerTires.TrailerBackRightInside);
  const colorFrontLeftInside   = onGetTireColor(VehicleWithTrailerTires.TrailerFrontLeftInside);
  const colorBackLeftInside    = onGetTireColor(VehicleWithTrailerTires.TrailerBackLeftInside);
  const colorFrontRightOutside = onGetTireColor(VehicleWithTrailerTires.TrailerFrontRightOutside);
  const colorBackRightOutside  = onGetTireColor(VehicleWithTrailerTires.TrailerBackRightOutside);

  const showTireFrontLeftOutside  =  clickedItem === VehicleWithTrailerTires.TrailerFrontLeftOutside;
  const showTireBackLeftOutside   =  clickedItem === VehicleWithTrailerTires.TrailerBackLeftOutside;
  const showTireFrontRightInside  =  clickedItem === VehicleWithTrailerTires.TrailerFrontRightInside;
  const showTireBackRightInside   =  clickedItem === VehicleWithTrailerTires.TrailerBackRightInside;
  const showTireFrontLeftInside   =  clickedItem === VehicleWithTrailerTires.TrailerFrontLeftInside;
  const showTireBackLeftInside    =  clickedItem === VehicleWithTrailerTires.TrailerBackLeftInside;
  const showTireFrontRightOutside =  clickedItem === VehicleWithTrailerTires.TrailerFrontRightOutside;
  const showTireBackRightOutside  =  clickedItem === VehicleWithTrailerTires.TrailerBackRightOutside;
  /* WHEELS */
  // Add as onClick handler to wheel hubs when we enable clicking
  const mouseWheelEvent   = generateEvents(onTireClick, onTireMouseOver);
  const onWheelFrontLeft  = mouseWheelEvent(VehicleWithTrailerWheels.HubFrontLeft);
  const onWheelFrontRight = mouseWheelEvent(VehicleWithTrailerWheels.HubFrontRight);
  const onWheelRearLeft   = mouseWheelEvent(VehicleWithTrailerWheels.HubRearLeft);
  const onWheelRearRight  = mouseWheelEvent(VehicleWithTrailerWheels.HubRearRight);

  const onGetWheelColor = (wheel) => {
    return getItemColor({
      type        : 'wheel',
      item        : wheel,
      issue       : issues.includes(wheel),
      clickedItem : hoveredItem || clickedItem
    }, sensorItemMapping);
  };

  const getSelectedStrokeColor = (item) => {
    return clickedItem === item || hoveredItem === item ? dlsColor('lightBlue') : 'none';
  };

  const colorWheelFrontLeft  = onGetWheelColor(VehicleWithTrailerWheels.HubFrontLeft);
  const colorWheelFrontRight = onGetWheelColor(VehicleWithTrailerWheels.HubFrontRight);
  const colorWheelRearLeft   = onGetWheelColor(VehicleWithTrailerWheels.HubRearLeft);
  const colorWheelRearRight  = onGetWheelColor(VehicleWithTrailerWheels.HubRearRight);

  const showWheelFrontLeftTooltip  = clickedItem === VehicleWithTrailerWheels.HubFrontLeft;
  const showWheelFrontRightTooltip = clickedItem === VehicleWithTrailerWheels.HubFrontRight;
  const showWheelRearLeftTooltip   = clickedItem === VehicleWithTrailerWheels.HubRearLeft;
  const showWheelRearRightTooltip  = clickedItem === VehicleWithTrailerWheels.HubRearRight;

  const translations = translate('common:smarterTrailer.tires.locations', {returnObjects: true});

  return (
    <>
      {/* Trailer Outline */}
      <rect width="291"
        height="125"
        rx="2.5"
        ry="2.5"
        transform="translate(.5 0.5)"
        fill={white}
        stroke={grey4}/>

      {/* Grey Rectancle Front */}
      <rect width="10"
        height="98"
        rx="0"
        ry="0"
        transform="translate(292 14)"
        fill={grey4}/>

      {/* Front Right Hub Stroke */}
      <rect id={VehicleWithTrailerWheels.HubFrontRight}
        width="12"
        height="14"
        rx="1"
        ry="1"
        transform="matrix(-.999972-.007492 0.007492-.999972 102.895105 121)"
        fill={grey6}
        stroke={getSelectedStrokeColor(VehicleWithTrailerWheels.HubFrontRight)}
        strokeWidth="6"/>

      {/* Front Right Hub */}
      <rect width="12"
        height="24.1379"
        rx="1"
        ry="1"
        transform="matrix(1 0 0-1 91 121.1379)"
        fill={colorWheelFrontRight}
        stroke={grey4}/>

      {/* Front Right Inside Tire Stroke */}
      <rect id={VehicleWithTrailerTires.TrailerFrontRightInside}
        width="34"
        height="12.931"
        rx="1"
        ry="1"
        transform="translate(80 86.5182)"
        fill={grey6}
        stroke={getSelectedStrokeColor(VehicleWithTrailerTires.TrailerFrontRightInside)}
        strokeWidth="6"/>

      {/* Front Right Outside Tire Stroke */}
      <rect id={VehicleWithTrailerTires.TrailerFrontRightOutside}
        width="34"
        height="12.931"
        rx="1"
        ry="1"
        transform="translate(80 102.034)"
        fill={grey6}
        stroke={getSelectedStrokeColor(VehicleWithTrailerTires.TrailerFrontRightOutside)}
        strokeWidth="6"/>

      {/* Bottom Left Assembly */}

      {/* Rear Right Hub / Axle Stroke */}
      <rect id={VehicleWithTrailerWheels.HubRearRight}
        width="12"
        height="14"
        rx="1"
        ry="1"
        transform="matrix(-.999972-.007492 0.007492-.999972 46.895105 121)"
        fill={colorWheelRearRight}
        stroke={getSelectedStrokeColor(VehicleWithTrailerWheels.HubRearRight)}
        strokeWidth="6"/>

      {/* Rear Right Hub / Axle */}
      <rect width="12"
        height="24.1379"
        rx="1"
        ry="1"
        transform="matrix(1 0 0-1 34.895105 121.1379)"
        fill={colorWheelRearRight}
        stroke={grey4}/>

      {/* Back Right Inside Tire Stroke */}
      <rect id={VehicleWithTrailerTires.TrailerBackRightInside}
        width="34"
        height="12.931"
        rx="1"
        ry="1"
        transform="translate(24 86.5182)"
        fill={grey6}
        stroke={getSelectedStrokeColor(VehicleWithTrailerTires.TrailerBackRightInside)}
        strokeWidth="6"/>

      {/* Back Right Outside Tire Stroke */}
      <rect id={VehicleWithTrailerTires.TrailerBackRightOutside}
        width="34"
        height="12.931"
        rx="1"
        ry="1"
        transform="translate(24 102.034)"
        fill={grey6}
        stroke={getSelectedStrokeColor(VehicleWithTrailerTires.TrailerBackRightOutside)}
        strokeWidth="6"/>

      {/* Back Left Assembly */}

      {/* Back Left Hub / Axle Stroke */}
      <rect id={VehicleWithTrailerWheels.HubRearLeft}
        width="12"
        height="14"
        rx="1"
        ry="1"
        transform="translate(35 5)"
        fill={grey6}
        stroke={getSelectedStrokeColor(VehicleWithTrailerWheels.HubRearLeft)}
        strokeWidth="6"/>

      {/* Back Left Hub / Axle */}
      <rect width="12"
        height="24.1379"
        rx="1"
        ry="1"
        transform="translate(35 4.8621)"
        fill={colorWheelRearLeft}
        stroke={grey4}/>

      {/* Back Left Outside Tire Stroke */}
      <rect id={VehicleWithTrailerTires.TrailerBackLeftOutside}
        width="34"
        height="12.931"
        rx="1"
        ry="1"
        transform="translate(24 11.0352)"
        fill={grey6}
        stroke={getSelectedStrokeColor(VehicleWithTrailerTires.TrailerBackLeftOutside)}
        strokeWidth="6"/>

      {/* Back Left Inside Tire Stroke */}
      <rect id={VehicleWithTrailerTires.TrailerBackLeftInside}
        width="34"
        height="12.931"
        rx="1"
        ry="1"
        transform="translate(24 26.5508)"
        fill={colorFrontLeftInside}
        stroke={getSelectedStrokeColor(VehicleWithTrailerTires.TrailerBackLeftInside)}
        strokeWidth="6"/>

      {/* Front Left Assembly */}

      {/* Front Left Hub / Axle Stroke */}
      <rect id={VehicleWithTrailerWheels.HubFrontLeft}
        width="12"
        height="14"
        rx="1"
        ry="1"
        transform="translate(91 5)"
        fill={colorWheelFrontLeft}
        stroke={getSelectedStrokeColor(VehicleWithTrailerWheels.HubFrontLeft)}
        strokeWidth="6"/>

      {/* Front Left Hub / Axle */}
      <rect width="12"
        height="24.1379"
        rx="1"
        ry="1"
        transform="translate(91 4.8621)"
        fill={colorWheelFrontLeft}
        stroke={grey4}/>

      {/* Front Left Outside Tire Stroke */}
      <rect id={VehicleWithTrailerTires.TrailerFrontLeftOutside}
        width="34"
        height="12.931"
        rx="1"
        ry="1"
        transform="translate(80 11.0352)"
        fill={grey6}
        stroke={getSelectedStrokeColor(VehicleWithTrailerTires.TrailerFrontLeftOutside)}
        strokeWidth="6"/>

      {/* Front Left Inside Tire Stroke */}
      <rect id={VehicleWithTrailerTires.TrailerFrontLeftInside}
        width="34"
        height="12.931"
        rx="1"
        ry="1"
        transform="translate(80 26.5508)"
        fill={grey6}
        stroke={getSelectedStrokeColor(VehicleWithTrailerTires.TrailerFrontLeftInside)}
        strokeWidth="6"/>

      {/* Back Left Outside Tire */}
      <rect width="34"
        height="12.931"
        rx="1"
        ry="1"
        transform="translate(24 11.0352)"
        fill={colorBackLeftOutside}
        strokeWidth="1"
        stroke={grey4}/>

      {/* Back Left Inside Tire */}
      <rect width="34"
        height="12.931"
        rx="1"
        ry="1"
        transform="translate(24 26.5508)"
        fill={colorBackLeftInside}
        strokeWidth="1"
        stroke={grey4}/>

      {/* Back Right Outside Tire */}
      <rect width="34"
        height="12.931"
        rx="1"
        ry="1"
        transform="matrix(1 0 0-1 24 114.965)"
        fill={colorBackRightOutside}
        strokeWidth="1"
        stroke={grey4}/>

      {/* Back Right Inside Tire */}
      <rect width="34"
        height="12.931"
        rx="1"
        ry="1"
        transform="matrix(1 0 0-1 24 99.4492)"
        fill={colorBackRightInside}
        strokeWidth="1"
        stroke={grey4}/>

      {/* Front Left Outside Tire */}
      <rect width="34"
        height="12.931"
        rx="1"
        ry="1"
        transform="translate(80 11.0352)"
        fill={colorFrontLeftOutside}
        strokeWidth="1"
        stroke={grey4}/>

      {/* Front Left Inside Tire */}
      <rect width="34"
        height="12.931"
        rx="1"
        ry="1"
        transform="translate(80 26.5508)"
        fill={colorFrontLeftInside}
        strokeWidth="1"
        stroke={grey4}/>

      {/* Front Right Outside Tire */}
      <rect width="34"
        height="12.931"
        rx="1"
        ry="1"
        transform="matrix(1 0 0-1 80 114.965)"
        fill={colorFrontRightOutside}
        stroke={grey4}/>

      {/* Front Right Inside Tire */}
      <rect width="34"
        height="12.931"
        rx="1"
        ry="1"
        transform="matrix(1 0 0-1 80 99.4492)"
        fill={colorFrontRightInside}
        stroke={grey4}/>

      {/* Front Right Inside Tire */}
      {isTPMS && (
        <TooltipWrapper anchorEl={anchorEl}
          open={showTireFrontRightInside}
          title={translations[VehicleWithTrailerTires.TrailerFrontRightInside]}>
          <rect onMouseOut={onTireMouseOut}
            onMouseOver={onFrontRightInside}
            onClick={onFrontRightInside}
            width="34"
            height="12.931"
            rx="0"
            ry="0"
            transform="matrix(1.045472 0 0 3.0906 79.226976 82.509574)"
            style={style}
            fill="transparent"/>
        </TooltipWrapper>
      )}

      {/* Rear Right Inside Tire */}
      {isTPMS && (
        <TooltipWrapper anchorEl={anchorEl}
          open={showTireBackRightInside}
          title={translations[VehicleWithTrailerTires.TrailerBackRightInside]}>
          <rect onMouseOut={onTireMouseOut}
            onMouseOver={onBackRightInside}
            onClick={onBackRightInside}
            width="34"
            height="12.931"
            rx="0"
            ry="0"
            transform="matrix(1.045472 0 0 3.0906 23.226976 82.051723)"
            style={style}
            fill="transparent"/>
        </TooltipWrapper>
      )}

      {/* Front Left Inside Tire*/}
      {isTPMS && (
        <TooltipWrapper anchorEl={anchorEl}
          open={showTireFrontLeftInside}
          title={translations[VehicleWithTrailerTires.TrailerFrontLeftInside]}>
          <rect onMouseOut={onTireMouseOut}
            onMouseOver={onFrontLeftInside}
            onClick={onFrontLeftInside}
            width="34"
            height="12.931"
            rx="0"
            ry="0"
            transform="matrix(1.045472 0 0 3.0906 79.226976 2.983923)"
            style={style}
            fill="transparent"/>
        </TooltipWrapper>
      )}

      {/* Back Left Inside Tire */}
      {isTPMS && (
        <TooltipWrapper anchorEl={anchorEl}
          open={showTireBackLeftInside}
          title={translations[VehicleWithTrailerTires.TrailerBackLeftInside]}>
          <rect onMouseOut={onTireMouseOut}
            onMouseOver={onBackLeftInside}
            onClick={onBackLeftInside}
            width="34"
            height="12.931"
            rx="0"
            ry="0"
            transform="matrix(1.045472 0 0 3.0906 23.226976 2.983923)"
            style={style}
            fill="transparent"/>
        </TooltipWrapper>
      )}

      {/* Front Right Hub */}
      {isWETS && (
        <TooltipWrapper anchorEl={anchorEl}
          open={showWheelFrontRightTooltip}
          title={translations[VehicleWithTrailerWheels.HubFrontRight]}>
          <rect onMouseOut={onTireMouseOut}
            onMouseOver={onWheelFrontRight}
            onClick={onWheelFrontRight}
            width="34"
            height="12.931"
            rx="0"
            ry="0"
            transform="matrix(1.045472 0 0 3.0906 79.226976 82.509574)"
            style={style}
            fill="transparent"/>
        </TooltipWrapper>
      )}

      {/* Rear Right Hub */}
      {isWETS && (
        <TooltipWrapper anchorEl={anchorEl}
          open={showWheelRearRightTooltip}
          title={translations[VehicleWithTrailerWheels.HubRearRight]}>
          <rect onMouseOut={onTireMouseOut}
            onMouseOver={onWheelRearRight}
            onClick={onWheelRearRight}
            width="34"
            height="12.931"
            rx="0"
            ry="0"
            transform="matrix(1.045472 0 0 3.0906 23.226976 82.051723)"
            style={style}
            fill="transparent"/>
        </TooltipWrapper>
      )}

      {/* Front Left Hub */}
      {isWETS && (
        <TooltipWrapper anchorEl={anchorEl}
          open={showWheelFrontLeftTooltip}
          title={translations[VehicleWithTrailerWheels.HubFrontLeft]}>
          <rect onMouseOut={onTireMouseOut}
            onMouseOver={onWheelFrontLeft}
            onClick={onWheelFrontLeft}
            width="34"
            height="12.931"
            rx="0"
            ry="0"
            transform="matrix(1.045472 0 0 3.0906 79.226976 2.983923)"
            style={style}
            fill="transparent"/>
        </TooltipWrapper>
      )}

      {/* Rear Left Hub */}
      {isWETS && (
        <TooltipWrapper anchorEl={anchorEl}
          open={showWheelRearLeftTooltip}
          title={translations[VehicleWithTrailerWheels.HubRearLeft]}>
          <rect onMouseOut={onTireMouseOut}
            onMouseOver={onWheelRearLeft}
            onClick={onWheelRearLeft}
            width="34"
            height="12.931"
            rx="0"
            ry="0"
            transform="matrix(1.045472 0 0 3.0906 23.226976 2.983923)"
            style={style}
            fill="transparent"/>
        </TooltipWrapper>
      )}

      {/* Front Right Outside Tire */}
      {isTPMS && (
        <TooltipWrapper anchorEl={anchorEl}
          open={showTireFrontRightOutside}
          title={translations[VehicleWithTrailerTires.TrailerFrontRightOutside]}>
          <rect id={VehicleWithTrailerTires.TrailerFrontRightOutside}
            onMouseOut={onTireMouseOut}
            onMouseOver={onFrontRightOutside}
            onClick={onFrontRightOutside}
            width="34"
            height="12.931"
            rx="0"
            ry="0"
            transform="matrix(1.077277 0 0 1.697336 78.686291 100.525877)"
            style={style}
            fill="transparent"/>
        </TooltipWrapper>
      )}

      {/* Back Right Outside Tire */}
      {isTPMS && (
        <TooltipWrapper anchorEl={anchorEl}
          open={showTireBackRightOutside}
          title={translations[VehicleWithTrailerTires.TrailerBackRightOutside]}>
          <rect onMouseOut={onTireMouseOut}
            onMouseOver={onBackRightOutside}
            onClick={onBackRightOutside}
            width="34"
            height="12.931"
            rx="0"
            ry="0"
            transform="matrix(1.077277 0 0 1.697336 22.686291 100.068026)"
            style={style}
            fill="transparent"/>
        </TooltipWrapper>
      )}

      {/* Front Left Outside Tire */}
      {isTPMS && (
        <TooltipWrapper anchorEl={anchorEl}
          open={showTireFrontLeftOutside}
          title={translations[VehicleWithTrailerTires.TrailerFrontLeftOutside]}>
          <rect onMouseOut={onTireMouseOut}
            onMouseOver={onFrontLeftOutside}
            onClick={onFrontLeftOutside}
            width="34"
            height="12.931"
            rx="0"
            ry="0"
            transform="matrix(1.077277 0 0 1.697336 78.686291 3.000226)"
            style={style}
            fill="transparent"/>
        </TooltipWrapper>
      )}

      {/* Back Left Outside Tire */}
      {isTPMS && (
        <TooltipWrapper anchorEl={anchorEl}
          open={showTireBackLeftOutside}
          title={translations[VehicleWithTrailerTires.TrailerBackLeftOutside]}>
          <rect onMouseOut={onTireMouseOut}
            onMouseOver={onBackLeftOutside}
            onClick={onBackLeftOutside}
            width="34"
            height="12.931"
            rx="0"
            ry="0"
            transform="matrix(1.077277 0 0 1.697336 22.686291 3.000226)"
            style={style}
            fill="transparent"/>
        </TooltipWrapper>
      )}
    </>
  );
};
